import { InfoIcon } from "@phosphor-icons/react";
import { type VariantProps, cva } from "class-variance-authority";
import type React from "react";
import { cloneElement, forwardRef, useState } from "react";
import {
  Tooltip as AriaTooltip,
  type TooltipProps as AriaTooltipProps,
  TooltipTrigger as AriaTooltipTrigger,
  Button,
  type ButtonProps,
  composeRenderProps,
} from "react-aria-components";
import { getTooltipStyles } from "./tooltip-styles";

// Use the AriaTooltipTrigger directly
const TooltipTrigger = (
  props: React.ComponentProps<typeof AriaTooltipTrigger>,
) => <AriaTooltipTrigger {...props} delay={props.delay ?? 200} />;
TooltipTrigger.displayName = "TooltipTrigger";

interface CustomTooltipProps extends AriaTooltipProps {
  align?: "left" | "right" | "center";
  variant?: "default" | "accessibility";
}

const Tooltip = forwardRef<HTMLDivElement, CustomTooltipProps>(
  (
    { className, offset = 4, align = "center", variant = "default", ...props },
    ref,
  ) => (
    <AriaTooltip
      ref={ref}
      offset={offset}
      className={composeRenderProps(
        className,
        (className) =>
          getTooltipStyles(variant, className) +
          (align === "left"
            ? " text-left"
            : align === "right"
              ? " text-right"
              : " text-center"),
      )}
      {...props}
    />
  ),
);

Tooltip.displayName = "Tooltip";

// InformationTooltip variants
const informationTooltipVariants = cva(
  "inline-flex items-center justify-center rounded-md transition-colors cursor-pointer",
  {
    variants: {
      variant: {
        default: "text-gray-600 hover:text-gray-700",
        subtle: "text-gray-400 hover:text-gray-600",
        primary: "text-blue-500 hover:text-blue-700",
        muted: "text-gray-300 hover:text-gray-500",
      },
      size: {
        sm: "w-6 h-6 p-1.5",
        md: "w-7 h-7 p-1.5",
        lg: "w-8 h-8 p-2",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "sm",
    },
  },
);

// Icon sizes mapping
const iconSizes = {
  sm: 12,
  md: 16,
  lg: 20,
} as const;

interface InformationTooltipProps
  extends Omit<ButtonProps, "children" | "aria-label">,
    VariantProps<typeof informationTooltipVariants> {
  /**
   * The content to display in the tooltip
   */
  content: React.ReactNode;
  /**
   * Optional label for accessibility (defaults to "Information")
   */
  "aria-label"?: string;
  /**
   * Optional class name for custom styling
   */
  className?: string;
  /**
   * Optional class name for tooltip styling
   */
  tooltipClassName?: string;
  /**
   * Optional custom icon (defaults to InfoIcon)
   */
  icon?: React.ReactElement;
  /**
   * Optional position for tooltip
   */
  placement?: "top" | "bottom" | "left" | "right";
  /**
   * Optional offset for tooltip
   */
  offset?: number;
  /**
   * Optional cross offset for tooltip
   */
  crossOffset?: number;
  /**
   * Optional tooltip variant
   */
  tooltipVariant?: "default" | "accessibility";
  /**
   * Optional tooltip alignment
   */
  align?: "left" | "right" | "center";
  /**
   * Whether the tooltip should stay open when clicked (defaults to false for hover behavior)
   */
  isClickable?: boolean;
  /**
   * Delay in milliseconds before showing the tooltip (inherits from TooltipTrigger defaults)
   */
  delay?: number;
  /**
   * Delay in milliseconds before hiding the tooltip (inherits from TooltipTrigger defaults)
   */
  closeDelay?: number;
}

/**
 * InformationTooltip component that displays an information icon with a tooltip
 * Uses Button as the trigger with customizable variants and sizes
 */
const InformationTooltip = forwardRef<
  HTMLButtonElement,
  InformationTooltipProps
>(
  (
    {
      content,
      variant,
      size,
      className,
      tooltipClassName,
      icon,
      "aria-label": ariaLabel = "Information",
      placement = "top",
      offset = 4,
      crossOffset = 0,
      tooltipVariant = "default",
      align = "center",
      isClickable = false,
      delay,
      closeDelay,
      ...props
    },
    ref,
  ) => {
    const [isOpen, setIsOpen] = useState(false);
    const IconComponent = icon || <InfoIcon />;

    const handlePress = () => {
      if (isClickable) {
        setIsOpen(!isOpen);
      }
    };

    return (
      <TooltipTrigger
        isOpen={isClickable ? isOpen : undefined}
        onOpenChange={isClickable ? setIsOpen : undefined}
        delay={isClickable ? 0 : delay}
        closeDelay={closeDelay}
      >
        <Button
          ref={ref}
          aria-label={ariaLabel}
          className={informationTooltipVariants({ variant, size, className })}
          onPress={isClickable ? handlePress : undefined}
          {...props}
        >
          {cloneElement(IconComponent, {
            size: IconComponent.props.size || iconSizes[size || "sm"],
          })}
        </Button>
        <Tooltip
          variant={tooltipVariant}
          align={align}
          offset={offset}
          crossOffset={crossOffset}
          className={tooltipClassName}
          placement={placement}
        >
          {content}
        </Tooltip>
      </TooltipTrigger>
    );
  },
);

InformationTooltip.displayName = "InformationTooltip";

export { Tooltip, TooltipTrigger, InformationTooltip };
export type { InformationTooltipProps };
